import React, { useState, useEffect, useRef } from 'react';
import UploadForm from './UploadForm';
import CreativesTable from './CreativesTable';
import PreviewDrawer from './PreviewDrawer';
import FilterBar from './FilterBar';
import api from '../../../services/api';
import useToast from './utils/useToast';
import { SlTabGroup, SlTab, SlTabPanel } from '@shoelace-style/shoelace/dist/react';
import { validateFiles, validateAudioFile, isValidUrl } from './utils/fileValidators';
import { isValid } from 'date-fns';

const PAGE_SIZE = 10;
const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'image', label: 'Cube' },
  { value: 'swiper', label: 'Swiper' },
  { value: 'upload_file', label: 'File Upload' },
  { value: 'video', label: 'Video' },
  { value: 'gif', label: 'GIF' }
];

const WebViewUpload = () => {
  const toast = useToast();

  const [creativeName, setCreativeName] = useState('');
  const [clickUrl, setClickUrl] = useState('');
  const [buttonUrl, setButtonUrl] = useState('');
  const [impressionUrl, setImpressionUrl] = useState('');
  const [secondaryImpressionUrl,setSecondaryImpressionUrl] = useState('');
  const [uploadType, setUploadType] = useState('image');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [audioFile, setAudioFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [removedFiles, setRemovedFiles] = useState([]);
  const [editSessionKey, setEditSessionKey] = useState(Date.now());
  const [shareOption, setShareOption] = useState(0);
  const [scriptCode, setScriptCode] = useState('');

  // Table and filter state
  const [tableData, setTableData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [previewDrawer, setPreviewDrawer] = useState({ open: false, row: null });
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [audioInputKey, setAudioInputKey] = useState(Date.now());
  const [filterType, setFilterType] = useState(TYPE_OPTIONS[0]);
  const [filterDateRange, setFilterDateRange] = useState({ startDate: null, endDate: null });
  const [filterSearch, setFilterSearch] = useState('');
  const [activeTab, setActiveTab] = useState('table');
  const [isSaving, setIsSaving] = useState(false);
  const tabGroupRef = useRef(null);

  // Video-specific state
  const [ctaText, setCtaText] = useState('');
  const [ctaBgColor, setCtaBgColor] = useState('');
  const [ctaTextColor, setCtaTextColor] = useState('');
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);

  // New video URL state
  const [videoInputType, setVideoInputType] = useState('file');
  const [videoUrl, setVideoUrl] = useState('');
  const [errors, setErrors] = useState({});

  // HTML upload sub-mode: 'html' (default) or 'any' (generic files to get URLs)
  const [uploadFileMode, setUploadFileMode] = useState('html');
  const [genericFileUrls, setGenericFileUrls] = useState([]); // [{name, url}]

  // --- API: Fetch creatives table ---
  const fetchTable = async (page = 0) => {
    setTableLoading(true);
    try {
      const payload = {
        page,
        limit: PAGE_SIZE,
        type: filterType.value || undefined,
        search: filterSearch || undefined,
        created_on: filterDateRange.startDate && filterDateRange.endDate
          ? { start: filterDateRange.startDate, end: filterDateRange.endDate }
          : undefined,
      };
      const res = await api({
        url: '/user/campaign_creation/getWebCreatives',
        method: 'POST',
        data: payload,
      });
      setTableData(res?.data?.data || []);
      setTotalRows(res?.data?.total || 0);
    } catch {
      toast('error', 'Failed to fetch creatives');
      setTableData([]);
      setTotalRows(0);
    }
    setTableLoading(false);
  };

  // --- EFFECTS: Table/filter effect logic ---
  useEffect(() => {
    setCurrentPage(0); // Reset to first page on filter change
  }, [filterType, filterDateRange, filterSearch]);

  useEffect(() => {
    fetchTable(currentPage);
  }, [currentPage, filterType, filterDateRange, filterSearch]);

  // --- FILE HANDLING: File and audio input logic ---
  const handleFileChange = (e) => {
    // Clear file-related errors when user selects files
    setErrors((prev) => ({ ...prev, files: '' }));
    const files = Array.from(e.target.files);
    if (uploadType === 'image' || uploadType === 'swiper') {
      // Keep already-uploaded images and pending files
      const existingUploaded = selectedFiles.filter(f => !f.file && f.status === 'success');
      const existingPending = selectedFiles.filter(f => f.file);
      // New files to add
      const newFiles = files.filter(file => {
        // Prevent duplicates by name/type
        return !existingPending.some(f => f.file && f.file.name === file.name && f.file.type === file.type)
          && !existingUploaded.some(f => f.url && f.url.endsWith(file.name));
      });
      const newFileObjs = newFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
        status: 'pending',
        url: '',
        progress: 0,
        isNew: true,
      }));
      setSelectedFiles([...existingUploaded, ...existingPending, ...newFileObjs]);
    } else if (uploadType === 'video') {
      if (videoInputType === 'file') {
        if (!validateFiles({ files, type: uploadType, Toast: toast })) return;
        const file = files[0];
        setSelectedFiles([{
          file,
          preview: URL.createObjectURL(file),
          status: 'pending',
          url: '',
          progress: 0,
          isNew: true,
        }]);
      }
    } else if (uploadType === 'gif') {
      if (!validateFiles({ files, type: 'gif', Toast: toast })) return;
      const file = files[0];
      setSelectedFiles([{
        file,
        preview: URL.createObjectURL(file),
        status: 'pending',
        url: '',
        progress: 0,
        isNew: true,
      }]);
    } else if (uploadType === 'upload_file') {
      if (uploadFileMode === 'any') {
        // Accept any files, allow multiple
        const newFileObjs = files.map((file) => ({
          file,
          preview: URL.createObjectURL(file),
          status: 'pending',
          url: '',
          progress: 0,
          isNew: true,
        }));
        setSelectedFiles(newFileObjs);
        setGenericFileUrls([]);
      } else {
        if (!validateFiles({ files, type: uploadType, Toast: toast })) return;
        const file = files[0];
        setSelectedFiles([{
          file,
          preview: URL.createObjectURL(file),
          status: 'pending',
          url: '',
          progress: 0,
          fileType: 'html',
          isNew: true,
        }]);
      }
    }
  };

  const handleAudioChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!validateAudioFile(file, toast)) return;

    setAudioFile({
      file,
      preview: URL.createObjectURL(file),
      status: 'pending',
      url: '',
      progress: 0,
      isNew: true,
    });
  };

  // --- Field setters that clear specific errors ---
  const onCreativeNameChange = (value) => {
    setCreativeName(value);
    setErrors((prev) => ({ ...prev, creativeName: '' }));
  };

  const onImpressionUrlChange = (value) => {
    setImpressionUrl(value);
    setErrors((prev) => ({ ...prev, impressionUrl: '' }));
  };
  const onSecondayUrlChange = (value) =>{
    setSecondaryImpressionUrl(value);
    setErrors((prev) => ({ ...prev,secondaryImpressionUrl: ''}))
  }
  const onVideoUrlChange = (value) => {
    setVideoUrl(value);
    setErrors((prev) => ({ ...prev, videoUrl: '' }));
  };

  const onCtaTextChange = (value) => {
    setCtaText(value);
    setErrors((prev) => ({ ...prev, ctaText: '' }));
  };

  const onButtonUrlChange = (value) => {
    setButtonUrl(value);
    setErrors((prev) => ({ ...prev, buttonUrl: '' }));
  };

  // --- Validation helpers ---
  const validateForUpload = () => {
    const newErrors = {};
    let valid = true;

    if (!creativeName.trim()) {
      newErrors.creativeName = 'Creative name is required';
      valid = false;
    }
    if (impressionUrl && !isValidUrl(impressionUrl)) {
      newErrors.impressionUrl = 'Enter a valid URL';
      valid = false;
    }
    if(secondaryImpressionUrl && !isValidUrl(secondaryImpressionUrl)) {
      newErrors.secondaryImpressionUrl = 'Enter a valid URL';
      valid = false;
    }

    if (uploadType === 'video' && videoInputType === 'url') {
      if (!videoUrl.trim()) {
        newErrors.videoUrl = 'Video URL is required';
        valid = false;
      } else if (!isValidUrl(videoUrl)) {
        newErrors.videoUrl = 'Enter a valid URL';
        valid = false;
      }
    } else {
      if (selectedFiles.length === 0 && !(audioFile && audioFile.isNew)) {
        let need = 'images';
        if (uploadType === 'upload_file') {
          need = uploadFileMode === 'any' ? 'file(s)' : 'an HTML file';
        } else if (uploadType === 'video') {
          need = 'a video file';
        }
        newErrors.files = `Please select ${need} or audio first`;
        valid = false;
      }
    }

    setErrors(newErrors);
    return valid;
  };

  const validateForSave = () => {
    const newErrors = {};
    let valid = true;

    if (!creativeName.trim()) {
      newErrors.creativeName = 'Creative name is required';
      valid = false;
    }
    if (impressionUrl && !isValidUrl(impressionUrl)) {
      newErrors.impressionUrl = 'Enter a valid URL';
      valid = false;
    }
    if (secondaryImpressionUrl && !isValidUrl(secondaryImpressionUrl)) {
      newErrors.secondaryImpressionUrl = 'Enter a valid URL';
      valid = false;
    }
    if (uploadType === 'video') {
      if (videoInputType === 'url') {
        if (!videoUrl.trim()) {
          newErrors.videoUrl = 'Video URL is required';
          valid = false;
        } else if (!isValidUrl(videoUrl)) {
          newErrors.videoUrl = 'Enter a valid URL';
          valid = false;
        }
      }
      if (isButtonEnabled) {
        if (!ctaText.trim()) {
          newErrors.ctaText = 'CTA text is required when button is enabled';
          valid = false;
        }
        if (!buttonUrl.trim()) {
          // Presence only; no URL format validation per requirements
          newErrors.buttonUrl = 'Button URL is required when button is enabled';
          valid = false;
        }
      }
    } else if (uploadType === 'upload_file') {
      if (!(selectedFiles[0] && selectedFiles[0].status === 'success')) {
        newErrors.files = 'Please upload the HTML file first';
        valid = false;
      }
    } else if (uploadType === 'gif' || uploadType === 'image' || uploadType === 'swiper') {
      // image/swiper
      if (selectedFiles.length === 0 || !selectedFiles.every((f) => f.status === 'success')) {
        newErrors.files = 'Please upload all files before saving';
        valid = false;
      }
    } else {
      // other types
    }

    if (audioFile && audioFile.isNew) {
      newErrors.files = 'Please upload audio file first';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const removeFile = (idx) => {
    setSelectedFiles((prev) => {
      const fileToRemove = prev[idx];
      if (fileToRemove && !fileToRemove.isNew && fileToRemove.url) {
        setRemovedFiles((rf) => [...rf, fileToRemove.url]);
      }
      if (fileToRemove?.preview) URL.revokeObjectURL(fileToRemove.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const removeAudioFile = () => {
    if (audioFile?.preview) {
      URL.revokeObjectURL(audioFile.preview);
    }
    setAudioFile(null);
    setAudioInputKey(Date.now()); // Reset the input
  };

  const uploadFiles = async () => {
    // Inline validation
    if (!validateForUpload()) return;

    if (uploadType === 'video' && videoInputType === 'url') {
      // For URL input, we don't need to upload, just validate (already validated above)
      setSelectedFiles([
        {
          file: null,
          preview: videoUrl,
          status: 'success',
          url: videoUrl,
          progress: 100,
          isNew: false,
        },
      ]);
      toast('success', 'Video URL validated successfully');
      return;
    }

    setIsUploading(true);
    try {
      if (uploadType === 'image' || uploadType === 'swiper') {
        // Bulk upload for images
        const filesToUpload = selectedFiles.filter(item => item.isNew);
        if (filesToUpload.length > 0) {
          const formData = new FormData();
          
          // Add all files to FormData
          filesToUpload.forEach((item, index) => {
            formData.append('files', item.file);
          });
          
          formData.append('creative_name', creativeName);
          formData.append('type', uploadType);
          
          // Update all files to uploading status
          setSelectedFiles(prev => prev.map(item => 
            item.isNew ? { ...item, status: 'uploading', progress: 0 } : item
          ));
          
          const res = await api({
            url: '/upload/webview/cube',
            method: 'POST',
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (data) => {
              const progress = Math.round((100 * data.loaded) / data.total);
              setSelectedFiles(prev => prev.map(item => 
                item.isNew ? { ...item, progress } : item
              ));
            }
          });
          
          if (res?.code === 200 && res?.data?.data) {
            // Handle bulk response - assuming API returns array of URLs
            const uploadedUrls = Array.isArray(res.data.data) ? res.data.data : [res.data.data];
            
            setSelectedFiles(prev => {
              const newFiles = [...prev];
              let urlIndex = 0;
              
              return newFiles.map(item => {
                if (item.isNew) {
                  const uploadedItem = {
                    ...item,
                    status: 'success',
                    url: uploadedUrls[urlIndex] || '',
                    progress: 100,
                    isNew: false
                  };
                  urlIndex++;
                  return uploadedItem;
                }
                return item;
              });
            });
            
            toast('success', `All ${filesToUpload.length} images uploaded successfully`);
          } else {
            setSelectedFiles(prev => prev.map(item => 
              item.isNew ? { ...item, status: 'error', url: '', progress: 0 } : item
            ));
            toast('error', 'Image upload failed');
          }
        }
      } else if (uploadType === 'gif') {
        // Bulk upload for GIFs using dedicated endpoint
        const filesToUpload = selectedFiles.filter(item => item.isNew);
        if (filesToUpload.length > 0) {
          const formData = new FormData();
          filesToUpload.forEach((item) => {
            formData.append('files', item.file);
          });
          formData.append('creative_name', creativeName);
          formData.append('type', 'gif');
          setSelectedFiles(prev => prev.map(item => item.isNew ? { ...item, status: 'uploading', progress: 0 } : item));
          const res = await api({
            url: '/upload/webview/gif',
            method: 'POST',
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (data) => {
              const progress = Math.round((100 * data.loaded) / data.total);
              setSelectedFiles(prev => prev.map(item => item.isNew ? { ...item, progress } : item));
            }
          });
          if (res?.code === 200 && res?.data?.data) {
            const uploadedUrls = Array.isArray(res.data.data) ? res.data.data : [res.data.data];
            setSelectedFiles(prev => {
              const newFiles = [...prev];
              let urlIndex = 0;
              return newFiles.map(item => item.isNew ? { ...item, status: 'success', url: uploadedUrls[urlIndex++] || '', progress: 100, isNew: false } : item);
            });
            toast('success', `All ${filesToUpload.length} GIFs uploaded successfully`);
          } else {
            setSelectedFiles(prev => prev.map(item => item.isNew ? { ...item, status: 'error', url: '', progress: 0 } : item));
            toast('error', 'GIF upload failed');
          }
        }
      } else if (uploadType === 'video') {
        if (selectedFiles[0]?.isNew) {
          const formData = new FormData();
          formData.append('files', selectedFiles[0].file);
          formData.append('creative_name', creativeName);
          formData.append('type', 'video');
          const res = await api({
            url: '/upload/webview/video',
            method: 'POST',
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (data) => {
              setSelectedFiles(prev => [{
                ...prev[0],
                status: 'uploading',
                progress: Math.round((100 * data.loaded) / data.total)
              }]);
            }
          });
          if (res?.code === 200 && res?.data?.data) {
            setSelectedFiles(prev => [{
              ...prev[0],
              status: 'success',
              url: res.data.data,
              progress: 100,
              isNew: false
            }]);
            toast('success', 'Video uploaded successfully');
          } else {
            setSelectedFiles(prev => [{
              ...prev[0],
              status: 'error',
              url: '',
              progress: 0
            }]);
            toast('error', 'Video upload failed');
          }
        }
      } else if (uploadType === 'upload_file') {
        if (uploadFileMode === 'any') {
          const filesToUpload = selectedFiles.filter(item => item.isNew);
          if (filesToUpload.length === 0) return;
          setSelectedFiles(prev => prev.map(item => item.isNew ? { ...item, status: 'uploading', progress: 0 } : item));
          const urls = [];
          for (let i = 0; i < filesToUpload.length; i++) {
            const item = filesToUpload[i];
            const formData = new FormData();
            formData.append('files', item.file);
            formData.append('creative_name', creativeName);
            formData.append('type', 'upload_file');
            try {
              const res = await api({
                url: '/upload/webview/html',
                method: 'POST',
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' },
              });
              if (res?.code === 200 && res?.data?.data) {
                urls.push({ name: item.file.name, url: res.data.data });
                setSelectedFiles(prev => prev.map(sf => (sf === item ? { ...sf, status: 'success', url: res.data.data, progress: 100, isNew: false } : sf)));
              } else {
                setSelectedFiles(prev => prev.map(sf => (sf === item ? { ...sf, status: 'error', url: '', progress: 0 } : sf)));
              }
            } catch {
              setSelectedFiles(prev => prev.map(sf => (sf === item ? { ...sf, status: 'error', url: '', progress: 0 } : sf)));
            }
          }
          setGenericFileUrls(urls);
          if (urls.length) toast('success', 'Files uploaded. URLs generated');
        } else if (selectedFiles[0]?.isNew) {
          const formData = new FormData();
          formData.append('files', selectedFiles[0].file);
          formData.append('creative_name', creativeName);
          formData.append('type', 'upload_file');
          const res = await api({
            url: '/upload/webview/html',
            method: 'POST',
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (data) => {
              setSelectedFiles(prev => [{
                ...prev[0],
                status: 'uploading',
                progress: Math.round((100 * data.loaded) / data.total)
              }]);
            }
          });
          if (res?.code === 200 && res?.data?.data) {
            setSelectedFiles(prev => [{
              ...prev[0],
              status: 'success',
              url: res.data.data,
              progress: 100,
              isNew: false
            }]);
            toast('success', 'HTML file uploaded successfully');
          } else {
            setSelectedFiles(prev => [{
              ...prev[0],
              status: 'error',
              url: '',
              progress: 0
            }]);
            toast('error', 'HTML file upload failed');
          }
        }
      }
      
      // Upload audio file if present and isNew
      if (audioFile && audioFile.file && audioFile.isNew) {
        try {
          const formDataAudio = new FormData();
          formDataAudio.append('files', audioFile.file);
          formDataAudio.append('creative_name', creativeName);
          formDataAudio.append('isAudio', true);
          const resAudio = await api({
            url: '/upload/webview/cube',
            method: 'POST',
            data: formDataAudio,
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (data) => {
              setAudioFile(prev => ({
                ...prev,
                status: 'uploading',
                progress: Math.round((100 * data.loaded) / data.total)
              }));
            }
          });
          if (resAudio?.code === 200 && resAudio?.data?.data) {
            setAudioFile(prev => ({
              ...prev,
              status: 'success',
              url: resAudio.data.data,
              progress: 100,
              isNew: false
            }));
            toast('success', 'Audio uploaded successfully');
          } else {
            setAudioFile(prev => ({ ...prev, status: 'error', url: '', progress: 0 }));
            toast('error', 'Audio upload failed');
          }
        } catch {
          setAudioFile(prev => ({ ...prev, status: 'error', url: '', progress: 0 }));
          toast('error', 'Audio upload failed');
        }
      }
    } catch {
      toast('error', 'Upload failed');
    }
    setIsUploading(false);
  };

  const handleEdit = (row) => {
    // Basic fields - use only API values
    console.log(row);
    setCreativeName(row.creative_name || '');
    setClickUrl(row.click_url || '');
    setButtonUrl(row.button_url || '');
    setImpressionUrl(row.impression_url || '');
    setSecondaryImpressionUrl(row.second_imp_url || '');
    setUploadType(row.type || 'image');
    setIsButtonEnabled(row.button_url?true:false);
    setEditId(row.id);
    setShareOption(row?.share || 0);
    setScriptCode(row.script || '');
    // Video-specific fields - only set if type is video
    if (row.type === 'video') {
      setCtaText(row.cta_text || '');
      setCtaBgColor(row.cta_bg_color || '');
      setCtaTextColor(row.cta_text_color || '');

      if (row.video_url) {
        setSelectedFiles([{
          file: null,
          preview: row.video_url,
          status: 'success',
          url: row.video_url,
          progress: 100,
          isNew: false,
        }]);
      }
    }
    // Image, Swiper or GIF type
    else if ((row.type === 'image' || row.type === 'swiper' || row.type === 'gif') && Array.isArray(row.img_urls)) {
      setSelectedFiles(row.img_urls.map((url) => ({
        file: null,
        preview: url,
        status: 'success',
        url,
        progress: 100,
        fileType: 'image',
        isNew: false,
      })));
    }
    // HTML file type
    else if (row.type === 'upload_file' && row.html_url) {
      setSelectedFiles([{
        file: null,
        preview: row.html_url,
        status: 'success',
        url: row.html_url,
        progress: 100,
        fileType: 'html',
        isNew: false,
      }]);
    }

    // Set audio file if exists
    if (row.audio_url) {
      setAudioFile({
        file: null,
        preview: row.audio_url,
        status: 'success',
        url: row.audio_url,
        progress: 100,
        isNew: false,
      });
    } else {
      setAudioFile(null);
    }

    setActiveTab('upload');
    setTimeout(() => {
      if (tabGroupRef.current && tabGroupRef.current.show) {
        tabGroupRef.current.show('upload');
      }
    }, 0);
    setRemovedFiles([]);
    setEditSessionKey(Date.now());
  };

  const resetForm = () => {
    setCreativeName('');
    setClickUrl('');
    setButtonUrl('');
    setImpressionUrl('');
    setSecondaryImpressionUrl('');
    setUploadType('image');
    setSelectedFiles([]);
    setAudioFile(null);
    setEditId(null);
    setFileInputKey(Date.now());
    setAudioInputKey(Date.now());
    // Reset video-specific fields to empty values
    setCtaText('');
    setCtaBgColor('');
    setCtaTextColor('');
    setIsButtonEnabled(false);
    setRemovedFiles([]);
    setVideoInputType('file');
    setVideoUrl('');
    setScriptCode('');
  };

  const saveCreative = async () => {
    try {
      setIsSaving(true);
      if (!validateForSave()) { setIsSaving(false); return; }

      let payload = {
        id: editId || null,
        creative_name: creativeName,
        click_url: clickUrl || null,
        button_url: buttonUrl || null,
        impression_url: impressionUrl || null,
        second_imp_url: secondaryImpressionUrl || null,
        type: uploadType,
        img_urls: (uploadType === 'image' || uploadType === 'swiper' || uploadType === 'gif')
          ? selectedFiles.filter(f => !removedFiles.includes(f.url)).map(f => f.url)
          : null,
        html_url: uploadType === 'upload_file'
          ? (selectedFiles[0] && !removedFiles.includes(selectedFiles[0].url) ? selectedFiles[0].url : null)
          : null,
        video_url: uploadType === 'video'
          ? (videoInputType === 'url' 
              ? videoUrl 
              : (selectedFiles[0] && !removedFiles.includes(selectedFiles[0].url) ? selectedFiles[0].url : null))
          : null,
        // Fix: audio_url should always be a string or null, not a stringified object
        audio_url: (audioFile && audioFile.status === 'success' && !removedFiles.includes(audioFile.url))
          ? audioFile.url
          : null,
        // Video-specific fields - only include if video type and button enabled
        show_button: uploadType === 'video' ? isButtonEnabled : null,
        cta_text: uploadType === 'video' && isButtonEnabled ? ctaText : null,
        cta_bg_color: uploadType === 'video' && isButtonEnabled ? ctaBgColor : null,
        cta_text_color: uploadType === 'video' && isButtonEnabled ? ctaTextColor : null,
        // Add share field
        share: shareOption,
        script: scriptCode,
      };

      try {
        const res = await api({
          url: '/user/campaign_creation/getWebHtml',
          method: 'POST',
          data: payload,
        });
        if (res?.code === 200 && res?.data?.html_url) {
          toast('success', `Creative ${editId ? 'updated' : 'saved'} successfully`);
          resetForm();
          fetchTable(currentPage);
        } else {
          toast('error', 'Failed to save creative');
        }
      } catch {
        toast('error', 'Failed to save creative');
      }
    } catch (err) {
      toast('error', err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <SlTabGroup ref={tabGroupRef}>
        <SlTab slot="nav" panel="upload"> Upload Creative</SlTab>
        <SlTab slot="nav" panel="table"> All Creatives</SlTab>

        <SlTabPanel name="upload" key={activeTab === 'upload'}>
          <UploadForm
            creativeName={creativeName}
            isSaving={isSaving}
            setCreativeName={onCreativeNameChange}
            clickUrl={clickUrl}
            setClickUrl={setClickUrl}
            buttonUrl={buttonUrl}
            setButtonUrl={onButtonUrlChange}
            impressionUrl={impressionUrl}
            setImpressionUrl={onImpressionUrlChange}
            uploadType={uploadType}
            setUploadType={setUploadType}
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
            audioFile={audioFile}
            setAudioFile={setAudioFile}
            isUploading={isUploading}
            uploadFiles={uploadFiles}
            saveCreative={saveCreative}
            editId={editId}
            resetForm={resetForm}
            handleFileChange={handleFileChange}
            handleAudioChange={handleAudioChange}
            removeFile={removeFile}
            removeAudioFile={removeAudioFile}
            fileInputKey={fileInputKey}
            audioInputKey={audioInputKey}
            // Video-specific props
            ctaText={ctaText}
            setCtaText={onCtaTextChange}
            ctaBgColor={ctaBgColor}
            setCtaBgColor={setCtaBgColor}
            ctaTextColor={ctaTextColor}
            setCtaTextColor={setCtaTextColor}
            isButtonEnabled={isButtonEnabled}
            setIsButtonEnabled={setIsButtonEnabled}
            editSessionKey={editSessionKey}
            videoInputType={videoInputType}
            setVideoInputType={setVideoInputType}
            videoUrl={videoUrl}
            setVideoUrl={onVideoUrlChange}
            shareOption={shareOption}
            scriptCode={scriptCode}
            setScriptCode={setScriptCode}
            setShareOption={setShareOption}
            errors={errors}
            uploadFileMode={uploadFileMode}
            setUploadFileMode={setUploadFileMode}
            genericFileUrls={genericFileUrls}
            secondaryImpressionUrl={secondaryImpressionUrl}
            setSecondaryImpressionUrl={onSecondayUrlChange}
          />
        </SlTabPanel>

        <SlTabPanel name="table" key={activeTab === 'table'}>
          <div className="p-6">
            <FilterBar
              filterType={filterType}
              setFilterType={setFilterType}
              filterDateRange={filterDateRange}
              setFilterDateRange={setFilterDateRange}
              filterSearch={filterSearch}
              setFilterSearch={setFilterSearch}
            />
            <CreativesTable
              tableData={tableData}
              tableLoading={tableLoading}
              totalRows={totalRows}
              PAGE_SIZE={PAGE_SIZE}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              handleEdit={handleEdit}
              setPreviewDrawer={setPreviewDrawer}
            />
          </div>
        </SlTabPanel>
      </SlTabGroup>

      <PreviewDrawer
        open={previewDrawer.open}
        row={previewDrawer.row}
        onClose={() => setPreviewDrawer({ open: false, row: null })}
      />
    </div>
  );
};

export default WebViewUpload;
